// React imports
import { useCallback, useEffect, useState, useMemo, useRef } from "react";

// Material UI imports
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Box,
  FormControlLabel,
  FormControl,
  Slider,
} from "@mui/material";
import { Add, Close } from "@mui/icons-material";

// ERP Common imports
import Button from "@/components/button/button";
import Modal from "@/components/modal/modal";
import Typography from "@/components/typography/typography";
import TextField from "@/components/text-field/text-field";
import Select from "@/components/select/select";
import Checkbox from "@/components/checkbox/checkbox";
import formatText from "@/utils/format-text";

// Local imports
import FieldSelect from "./field-select";
import { IElementJson } from "../custom-form";
import { ApiFormFields } from "../../Redux/state";
import "../custom-form.scss";

interface Option {
  label: string;
  value: string;
}

interface ITextEditFormProps {
  handleWidthChange: (value?: number) => void;
  handleColumnSelect: (value?: number) => void;
  handlePropertyValueUpdate: (
    event?: React.ChangeEvent<HTMLInputElement>,
    numberValue?: number,
    value?: any,
    fieldType?: string,
  ) => void;
  element: IElementJson | null;
  fields: ApiFormFields | null;
}

// Constants moved outside to prevent re-creation
const COLUMN_PER_ROW_OPTIONS = [1, 2, 3, 4, 5, 6] as const;
const SELECT_OPTIONS = [
  { text: "12 Hour", value: "12hr" },
  { text: "24 Hour", value: "24hr" },
] as const;
const CURRENCY_OPTIONS = [
  { text: "INR (India)", value: "inr" },
  { text: "USD (US)", value: "usd" },
] as const;

const FIELDS_WITHOUT_PLACEHOLDER = new Set([
  "time", "file", "geoLocation", "image", "info", "relation"
]);
const FIELDS_WITH_MIN_MAX = new Set(["text", "number"]);

// Memoized components to prevent unnecessary re-renders
const MemoizedMenuItem = ({ option, index, prefix }: { option: any; index: number; prefix: string }) => (
  <MenuItem key={`${prefix}-${index}`} value={option.value}>
    {option.text || option.label}
  </MenuItem>
);

const MemoizedRelationMenuItem = ({ option, index, prefix }: { option: Option; index: number; prefix: string }) => (
  <MenuItem key={`${prefix}-${index}`} value={option.value}>
    <Typography type="s3" color="theme.secondary.1000">
      {option.label}
    </Typography>
  </MenuItem>
);

export function TextEditForm(props: ITextEditFormProps) {
  const { 
    handlePropertyValueUpdate, 
    handleColumnSelect, 
    handleWidthChange, 
    fields, 
    element 
  } = props;

  // State management - optimized to prevent unnecessary re-renders
  const [elementJSON, setElementJSON] = useState<IElementJson | null>(element);
  const [openModal, setOpenModal] = useState(false);
  const [relationFields, setRelationFields] = useState<Option[]>([]);
  const [functionValue, setFunctionValue] = useState("");
  
  // Use refs to track previous values and prevent unnecessary updates
  const prevElementRef = useRef<IElementJson | null>(element);
  const prevFieldsRef = useRef<ApiFormFields | null>(fields);

  // Memoized relation modules with deep comparison prevention
  const relationModulesOptions = useMemo(() => {
    if (!fields || fields === prevFieldsRef.current) {
      if (prevFieldsRef.current && fields) return prevFieldsRef.current;
    }
    
    prevFieldsRef.current = fields;
    
    if (!fields) return [];
    return Object.keys(fields).map((key) => ({
      label: formatText(key),
      value: key,
    }));
  }, [fields]);

  // Optimized relation fields setter with memoization
  const setRelationFieldsOptions = useCallback(
    (moduleValue?: string) => {
      if (!fields || !moduleValue) {
        setRelationFields(prev => prev.length > 0 ? [] : prev);
        return;
      }

      const fieldsArr = fields[moduleValue];
      if (fieldsArr?.length) {
        const options = fieldsArr.map((field) => ({
          label: field.field,
          value: field.field,
        }));
        
        // Only update if options actually changed
        setRelationFields(prev => {
          if (prev.length !== options.length) return options;
          const isDifferent = prev.some((item, idx) => 
            item.value !== options[idx]?.value || item.label !== options[idx]?.label
          );
          return isDifferent ? options : prev;
        });
      } else {
        setRelationFields(prev => prev.length > 0 ? [] : prev);
      }
    },
    [fields]
  );

  // Memoized event handlers to prevent child re-renders
  const handleSave = useCallback((value: string) => {
    if (!elementJSON) return;
    
    // Only update if value actually changed
    if (elementJSON.default_value !== value) {
      const updatedElement = { ...elementJSON, default_value: value };
      setElementJSON(updatedElement);
      handlePropertyValueUpdate(null, null, value);
    }
    
    if (value) {
      setFunctionValue("");
      setOpenModal(false);
    }
  }, [elementJSON, handlePropertyValueUpdate]);

  const handleFieldUpdate = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!elementJSON) return;
    
    const fieldType = e.target.value;
    if (elementJSON.field_type !== fieldType) {
      const updatedElement = { ...elementJSON, field_type: fieldType };
      setElementJSON(updatedElement);
      handlePropertyValueUpdate(null, null, null, e.target.value);
    }
  }, [elementJSON, handlePropertyValueUpdate]);

  const handleSelectUpdate = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!elementJSON) return;
    
    const option = e.target.value;
    if (elementJSON.option !== option) {
      const updatedElement = { ...elementJSON, option };
      setElementJSON(updatedElement);
      handlePropertyValueUpdate(null, null, option);
    }
  }, [elementJSON, handlePropertyValueUpdate]);

  const handleModuleSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const moduleValue = e.target.value;
    if (elementJSON?.module !== moduleValue) {
      setRelationFieldsOptions(moduleValue);
      handlePropertyValueUpdate(e);
    }
  }, [elementJSON?.module, setRelationFieldsOptions, handlePropertyValueUpdate]);

  const handleWidthSliderChange = useCallback((_: Event, value: number | number[]) => {
    if (!elementJSON) return;
    
    const widthValue = Array.isArray(value) ? value[0] : value;
    if (elementJSON.custom_width !== widthValue) {
      const updatedElement = { ...elementJSON, custom_width: widthValue };
      setElementJSON(updatedElement);
      handleWidthChange(widthValue);
    }
  }, [elementJSON, handleWidthChange]);

  const handleFullWidthChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!elementJSON) return;
    
    const isFullWidth = e.target.checked;
    if (elementJSON.is_fullwidth !== isFullWidth) {
      const updatedElement = { ...elementJSON, is_fullwidth: isFullWidth };
      setElementJSON(updatedElement);
      handlePropertyValueUpdate(e);
    }
  }, [elementJSON, handlePropertyValueUpdate]);

  const handleColumnSelectChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const columnValue = Number(e.target.value);
    handleColumnSelect(columnValue);
  }, [handleColumnSelect]);

  // Memoized helper functions
  const getDefaultValue = useCallback((elementJSON: IElementJson) => {
    if (["number", "currency", "geoLocation"].includes(elementJSON.field_type)) {
      return elementJSON.default_value?.toString().replace(/[^0-9]/g, "") || "";
    }
    return elementJSON.default_value || "";
  }, []);

  const getMinMaxLabels = useCallback((fieldType: string) => {
    return fieldType === "text" 
      ? { min: "Min character count (with spaces)", max: "Max character count (with spaces)" }
      : { min: "Min number", max: "Max number" };
  }, []);

  // Modal handlers
  const handleOpenModal = useCallback(() => setOpenModal(true), []);
  const handleCloseModal = useCallback(() => {
    setOpenModal(false);
    setFunctionValue("");
  }, []);

  const handleFunctionValueChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFunctionValue(e.target.value);
  }, []);

  // Optimized effects with proper dependency arrays
  useEffect(() => {
    // Only update if element actually changed
    if (element !== prevElementRef.current) {
      setElementJSON(element);
      prevElementRef.current = element;
      
      if (element?.module) {
        setRelationFieldsOptions(element.module);
      }
    }
  }, [element, setRelationFieldsOptions]);

  // Memoized render sections to prevent unnecessary re-computation
  const currencySelectSection = useMemo(() => {
    if (elementJSON?.field_type !== "currency") return null;
    
    return (
      <Box>
        <FormControl fullWidth>
          <Select
            fullWidth
            placeholder="Select Currency"
            size="small"
            onChange={handleSelectUpdate}
            defaultValue={elementJSON.option}
            label="Currency"
            name="option"
            variant="outlined"
          >
            {CURRENCY_OPTIONS.map((option, index) => (
              <MemoizedMenuItem 
                key={`currency-${index}`}
                option={option} 
                index={index} 
                prefix="currency" 
              />
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  }, [elementJSON?.field_type, elementJSON?.option, handleSelectUpdate]);

  const timeSelectSection = useMemo(() => {
    if (elementJSON?.field_type !== "time") return null;
    
    return (
      <Box>
        <FormControl fullWidth>
          <Select
            fullWidth
            placeholder="Select Time Format"
            size="small"
            onChange={handleSelectUpdate}
            defaultValue={elementJSON.option}
            label="Time Format"
            name="option"
            variant="outlined"
          >
            {SELECT_OPTIONS.map((option, index) => (
              <MemoizedMenuItem 
                key={`time-${index}`}
                option={option} 
                index={index} 
                prefix="time" 
              />
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  }, [elementJSON?.field_type, elementJSON?.option, handleSelectUpdate]);

  const relationSelectSection = useMemo(() => {
    if (elementJSON?.field_type !== "relation") return null;
    
    return (
      <>
        <Box>
          <FormControl fullWidth>
            <Select
              fullWidth
              placeholder={
                relationModulesOptions.length
                  ? "Select Relation Module"
                  : "No data available"
              }
              size="small"
              onChange={handleModuleSelect}
              value={elementJSON.module || ""}
              label="Relation Module"
              name="module"
            >
              {relationModulesOptions.length ? (
                relationModulesOptions.map((option, index) => (
                  <MemoizedRelationMenuItem 
                    key={`module-${index}`}
                    option={option} 
                    index={index} 
                    prefix="module" 
                  />
                ))
              ) : (
                <MenuItem disabled>
                  <Typography type="s3" color="theme.secondary.1000">
                    <em>No data available</em>
                  </Typography>
                </MenuItem>
              )}
            </Select>
          </FormControl>
        </Box>

        <Box>
          <FormControl fullWidth>
            <Select
              fullWidth
              placeholder={
                !elementJSON.module
                  ? "Select Module First"
                  : relationFields.length
                  ? "Select Relation Field"
                  : "No data available"
              }
              size="small"
              onChange={handlePropertyValueUpdate}
              value={elementJSON.field || ""}
              label="Relation Field"
              disabled={!elementJSON.module}
              name="field"
            >
              {relationFields.length ? (
                relationFields.map((option, index) => (
                  <MemoizedRelationMenuItem 
                    key={`field-${index}`}
                    option={option} 
                    index={index} 
                    prefix="field" 
                  />
                ))
              ) : (
                <MenuItem disabled>
                  <Typography type="s3" color="theme.secondary.1000">
                    <em>No data available</em>
                  </Typography>
                </MenuItem>
              )}
            </Select>
          </FormControl>
        </Box>
      </>
    );
  }, [
    elementJSON?.field_type, 
    elementJSON?.module, 
    elementJSON?.field,
    relationModulesOptions, 
    relationFields, 
    handleModuleSelect, 
    handlePropertyValueUpdate
  ]);

  // Early return if no element
  if (!elementJSON) {
    return null;
  }

  const { field_type } = elementJSON;
  const minMaxLabels = getMinMaxLabels(field_type);

  return (
    <Box className="formSection--FieldContainer">
      {/* Field Type Selection */}
      <Box>
        <FieldSelect
          variant="outlined"
          onChange={handleFieldUpdate}
          defaultValue={field_type}
          label="Field Type"
          disabled={elementJSON.type === "system" || !elementJSON.is_new}
        />
      </Box>

      {/* Label Field */}
      <Box>
        <TextField
          fullWidth
          label="Label"
          is_required
          name="label"
          type="text"
          onChange={handlePropertyValueUpdate}
          defaultValue={elementJSON.label}
        />
      </Box>

      {/* File/Image Max Size */}
      {(field_type === "file" || field_type === "image") && (
        <Box>
          <TextField
            fullWidth
            label="Max File Size (in MB)"
            name="max_size"
            type="number"
            onChange={handlePropertyValueUpdate}
            defaultValue={elementJSON.max_size}
          />
        </Box>
      )}

      {/* Currency Selection - Memoized */}
      {currencySelectSection}

      {/* Placeholder and Default Value */}
      {!FIELDS_WITHOUT_PLACEHOLDER.has(field_type) && (
        <>
          <Box>
            <TextField
              fullWidth
              label="Placeholder"
              name="placeholder"
              type="text"
              onChange={handlePropertyValueUpdate}
              defaultValue={elementJSON.placeholder}
            />
          </Box>
          <Box>
            <TextField
              fullWidth
              label="Default Value"
              placeholder="Default Value"
              name="default_value"
              type={field_type === "phone" ? "tel" : field_type === "number" ? "number" : "text"}
              onChange={handlePropertyValueUpdate}
              defaultValue={getDefaultValue(elementJSON)}
            />
          </Box>
        </>
      )}

      {/* Time Format Selection - Memoized */}
      {timeSelectSection}

      {/* Relation Module and Field Selection - Memoized */}
      {relationSelectSection}

      {/* Min/Max Length for Text and Number Fields */}
      {FIELDS_WITH_MIN_MAX.has(field_type) && (
        <>
          <Box>
            <TextField
              fullWidth
              label={minMaxLabels.min}
              name="min_length"
              type="number"
              onChange={(e) => handlePropertyValueUpdate(e, Number(e.target.value))}
              defaultValue={elementJSON.min_length || elementJSON.min || 1}
            />
          </Box>
          <Box>
            <TextField
              fullWidth
              label={minMaxLabels.max}
              name="max_length"
              type="number"
              onChange={(e) => handlePropertyValueUpdate(e, Number(e.target.value))}
              defaultValue={
                elementJSON.max_length || 
                elementJSON.max || 
                (field_type === 'number' ? 10000 : 255)
              }
            />
          </Box>
        </>
      )}

      {/* Column Per Row Selection */}
      <Box>
        <FormControl fullWidth>
          <Select
            fullWidth
            placeholder="Select Column Per Row"
            size="small"
            onChange={handleColumnSelectChange}
            value={elementJSON.columns_per_row || 1}
            label="Select Column Per Row"
            name="columns_per_row"
            variant="outlined"
          >
            {COLUMN_PER_ROW_OPTIONS.map((option, index) => (
              <MenuItem key={`column-${index}`} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Width Adjustment Slider */}
      <Box>
        <Typography sx={{ color: 'black', mb: 1 }}>
          Adjust Width:
        </Typography>
        <Slider
          value={elementJSON.custom_width || 12}
          name="custom_width"
          min={1}
          max={12}
          step={1}
          onChange={handleWidthSliderChange}
          aria-labelledby="width-slider"
          sx={{ color: "#4AC08C" }}
          disabled={elementJSON.is_fullwidth}
          marks
          valueLabelDisplay="auto"
        />
      </Box>

      {/* Number Field Specific Options */}
      {field_type === "number" && (
        <>
          <Box>
            <TextField
              fullWidth
              label="Float Step"
              name="float_step"
              type="number"
              step="0.01"
              onChange={handlePropertyValueUpdate}
              defaultValue={elementJSON.float_step}
            />
          </Box>
          <Box>
            <FormControlLabel
              name="is_negative"
              control={<Checkbox />}
              onChange={handlePropertyValueUpdate}
              checked={elementJSON.is_negative || false}
              label="Allow Negative Numbers"
            />
          </Box>
        </>
      )}

      {/* Date Field Specific Options */}
      {field_type === "date" && (
        <>
          <Box>
            <FormControlLabel
              name="is_future_dates_allowed"
              control={<Checkbox />}
              onChange={handlePropertyValueUpdate}
              checked={elementJSON.is_future_dates_allowed || false}
              label="Allow Future Dates"
            />
          </Box>
          <Box>
            <FormControlLabel
              name="is_past_dates_allowed"
              control={<Checkbox />}
              onChange={handlePropertyValueUpdate}
              checked={elementJSON.is_past_dates_allowed || false}
              label="Allow Past Dates"
            />
          </Box>
        </>
      )}

      {/* Required Field Checkbox */}
      {field_type !== "info" && (
        <Box>
          <FormControlLabel
            name="is_required"
            control={<Checkbox />}
            onChange={handlePropertyValueUpdate}
            checked={elementJSON.is_required || false}
            label="This field is required"
          />
        </Box>
      )}

      {/* Email Unique Checkbox */}
      {field_type === "email" && (
        <Box>
          <FormControlLabel
            name="is_unique"
            control={<Checkbox />}
            onChange={handlePropertyValueUpdate}
            checked={elementJSON.is_unique || false}
            label="Is Unique"
          />
        </Box>
      )}

      {/* Text Multiline Checkbox */}
      {field_type === "text" && (
        <Box>
          <FormControlLabel
            name="is_multiline"
            control={<Checkbox />}
            onChange={handlePropertyValueUpdate}
            checked={elementJSON.is_multiline || false}
            label="Is Multiline"
          />
        </Box>
      )}

      {/* Full Width Checkbox */}
      <Box>
        <FormControlLabel
          name="is_fullwidth"
          control={<Checkbox />}
          onChange={handleFullWidthChange}
          checked={elementJSON.is_fullwidth || false}
          label="Full Width"
        />
      </Box>

      {/* Info Field Function Button */}
      {field_type === "info" && (
        <Box>
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={handleOpenModal}
            className="formSection--PreviewButton"
          >
            Add Function
          </Button>
        </Box>
      )}

      {/* Function Modal */}
      <Modal fullWidth onClose={handleCloseModal} open={openModal}>
        <DialogTitle>
          <Typography type="s3" weight="medium" color="theme.secondary.1000">
            Add Function
          </Typography>
          <IconButton
            edge="end"
            onClick={handleCloseModal}
            size="small"
          >
            <Close fontSize="inherit" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography weight="medium" type="s3" color="theme.secondary.700" sx={{ mb: 2 }}>
            Label: <span style={{ color: "#1F2125" }}>{elementJSON.label}</span>
          </Typography>
          <TextField
            name="default_value"
            fullWidth
            placeholder="Add a function"
            multiline
            minRows={4}
            defaultValue={elementJSON.default_value}
            onChange={handleFunctionValueChange}
            value={functionValue}
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={handleCloseModal}
            style={{ color: "black" }}
          >
            Cancel
          </Button>
          <Button onClick={() => handleSave(functionValue)}>
            Save
          </Button>
        </DialogActions>
      </Modal>
    </Box>
  );
}

export default TextEditForm;